import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import CalendarGrid from '../components/calendar/CalendarGrid';
import { useCalendar } from '../hooks/useCalendar';
import { useMsalAuth } from '../context/MsalAuthContext';
import { m365CalendarAPI } from '../services/m365Api';
import { formatDate, parseISOString } from '../utils/timeUtils';
import { CALENDAR_SETTINGS } from '../utils/constants';
import EmptyState from '../components/common/EmptyState';

/**
 * Microsoft 365 カレンダービューページ（閲覧専用）
 *
 * Azure ADでログインしたユーザーが会議室カレンダーを閲覧するためのページ。
 * 予約の作成・編集・削除機能は無効。
 */
const M365CalendarPage = () => {
  const gridRef = useRef(null);
  const savedScrollPosition = useRef(null);
  const { isAuthenticated, loading: authLoading, sessionExpired, tokenReady } = useMsalAuth();
  const { currentDate, viewMode, goToToday, goToPrevious, goToNext, getTitle, getWeekDates, getDateRange, setView } = useCalendar();

  const dateRange = useMemo(() => getDateRange(), [getDateRange]);
  const weekDates = useMemo(() => getWeekDates(), [getWeekDates]);

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [slideDirection, setSlideDirection] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  /**
   * M365カレンダーデータを取得
   * @param {Object} options
   * @param {boolean} options.silent - trueの場合ローディング表示を抑制（ポーリング用）
   */
  const fetchCalendar = useCallback(async ({ silent = false } = {}) => {
    if (!dateRange.start || !dateRange.end) return;
    if (!isAuthenticated) return;

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      // 週の各日付の月を収集
      const monthsToFetch = new Set();
      const currentDate = new Date(dateRange.start);
      while (currentDate <= dateRange.end) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        monthsToFetch.add(`${year}-${month}`);
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // 各月のデータを取得
      let allEvents = [];
      for (const monthKey of monthsToFetch) {
        const [year, month] = monthKey.split('-').map(Number);
        try {
          const response = await m365CalendarAPI.getCalendarByMonth(year, month);
          if (response.events) {
            allEvents = [...allEvents, ...response.events];
          }
        } catch (err) {
          // 認証エラー・セッション切れは握りつぶさず上位に伝播
          if (err.isSessionExpired || err.isUnauthorized || err.isForbidden) {
            throw err;
          }
          console.error(`Failed to fetch month ${year}/${month}:`, err);
        }
      }

      // イベントを内部形式に変換
      const parsedEvents = allEvents.map((event) => {
        const startISO = event.start_datetime;
        const endISO = event.end_datetime;
        return {
          ...event,
          id: event.id,
          reservation_id: event.id,
          start_datetime: startISO,
          end_datetime: endISO,
          start: parseISOString(startISO),
          end: parseISOString(endISO),
          editable: false,
        };
      });

      // 重複を除去
      const uniqueEvents = [];
      const seenIds = new Set();
      for (const event of parsedEvents) {
        if (!seenIds.has(event.id)) {
          seenIds.add(event.id);
          uniqueEvents.push(event);
        }
      }

      setReservations(uniqueEvents);
    } catch (err) {
      console.error('Failed to fetch M365 calendar:', err);
      if (err.isSessionExpired) {
        // MsalAuthContext の sessionExpired → loginRedirect が自動処理する
        // エラーメッセージは設定しない（"再接続中..." バナーが表示される）
      } else if (err.isUnauthorized) {
        setError('セッションの有効期限が切れました。再ログインしてください。');
      } else if (err.isForbidden) {
        setError(err.message);
      } else {
        setError('カレンダーの取得に失敗しました');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [dateRange, isAuthenticated]);

  // 日付範囲が変更��れたら、または認証完了後/再ログイン後に���レンダーを再取得
  // tokenReady: トークンプロバイダーの登録完了を待つ（useEffect実行順序の競合回避）
  // sessionExpired が false に戻ったら（再ログイン成功時）自動的に再取得される
  useEffect(() => {
    if (isAuthenticated && tokenReady && !sessionExpired) {
      fetchCalendar();
    }
  }, [fetchCalendar, isAuthenticated, tokenReady, sessionExpired]);

  // 60秒���隔でカレンダーを自動更新（ポーリング）
  // タブが非表示の間はポーリングを停止し、再表示時に即座に再取得する
  useEffect(() => {
    if (!isAuthenticated || !tokenReady || sessionExpired) return;

    const POLL_INTERVAL_MS = 60 * 1000;
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchCalendar({ silent: true });
      }
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCalendar({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchCalendar, isAuthenticated, tokenReady, sessionExpired]);

  /**
   * 現在時刻に基づいてカレンダーを自動スクロール
   */
  const scrollToCurrentTime = () => {
    if (!gridRef.current) return;

    const { START_HOUR, END_HOUR, GRID_ROW_HEIGHT } = CALENDAR_SETTINGS;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();

    let targetHour = currentHour;
    let targetMinutes = currentMinutes;

    if (currentHour < START_HOUR) {
      targetHour = START_HOUR;
      targetMinutes = 0;
    } else if (currentHour >= END_HOUR) {
      targetHour = START_HOUR;
      targetMinutes = 0;
    }

    const scrollPosition =
      ((targetHour - START_HOUR) * 60 + targetMinutes) / 60 * GRID_ROW_HEIGHT;

    const offsetTop = 150;
    const adjustedScroll = Math.max(0, scrollPosition - offsetTop);

    gridRef.current.scrollTop = adjustedScroll;
  };

  // スクロール位置を保存
  const handleScroll = () => {
    if (gridRef.current) {
      savedScrollPosition.current = gridRef.current.scrollTop;
    }
  };

  // グリッド高さの計算
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    let isInitialLoad = true;

    const updateHeight = () => {
      if (!gridRef.current) return;
      const rect = gridRef.current.getBoundingClientRect();
      const heightPx = Math.max(0, window.innerHeight - rect.top);
      gridRef.current.style.setProperty('--grid-height', `${heightPx}px`);

      if (isInitialLoad) {
        requestAnimationFrame(() => {
          scrollToCurrentTime();
          savedScrollPosition.current = gridRef.current?.scrollTop || 0;
        });
        isInitialLoad = false;
      }
    };

    const raf = requestAnimationFrame(updateHeight);
    window.addEventListener('resize', updateHeight);
    el.addEventListener('scroll', handleScroll);

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    ro.observe(gridRef.current);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateHeight);
      el.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, []);

  // 週切り替え時にスクロール位置を復元
  useEffect(() => {
    if (!gridRef.current) return;
    if (savedScrollPosition.current !== null && savedScrollPosition.current !== undefined) {
      requestAnimationFrame(() => {
        if (gridRef.current) {
          gridRef.current.scrollTop = savedScrollPosition.current;
        }
      });
    }
  }, [dateRange]);

  // スライドアニメーションのリセット
  useEffect(() => {
    if (slideDirection) {
      const timer = setTimeout(() => setSlideDirection(null), 300);
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  // 初回ロード完了
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  const handleGoToPrevious = () => {
    setSlideDirection('left');
    goToPrevious();
  };

  const handleGoToNext = () => {
    setSlideDirection('right');
    goToNext();
  };

  // ログインしていない場合はログインページへ
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 予約クリック時のハンドラ（閲覧専用のため何もしない、または詳細表示）
  const handleReservationClick = (reservation) => {
    // 閲覧専用のため、クリックしても編集モーダルは開かない
    // 必要に応じて読み取り専用の詳細表示を実装可能
    console.log('Reservation clicked (read-only):', reservation);
  };

  return (
    <div className="calendar-page">
      {/* カレンダーヘッダー */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          <button onClick={handleGoToPrevious} className="nav-button" aria-label="前の週へ移動">
            ◀ 前週
          </button>
          <button onClick={goToToday} className="nav-button-today" aria-label="今週に戻る">
            今週
          </button>
          <button onClick={handleGoToNext} className="nav-button" aria-label="次の週へ移動">
            翌週 ▶
          </button>
          <div className="view-mode-toggle" role="group" aria-label="表示モード選択">
            <button
              onClick={() => setView('workweek')}
              className={viewMode === 'workweek' ? 'view-button active' : 'view-button'}
              aria-label="稼働日表示（月曜日から金曜日）"
              aria-pressed={viewMode === 'workweek'}
            >
              稼働日
            </button>
            <button
              onClick={() => setView('fullweek')}
              className={viewMode === 'fullweek' ? 'view-button active' : 'view-button'}
              aria-label="週表示（月曜日から日曜日）"
              aria-pressed={viewMode === 'fullweek'}
            >
              週
            </button>
          </div>
        </div>

        <div className="calendar-title">
          <h2 aria-live="polite" aria-atomic="true">{getTitle()}</h2>
        </div>

        {/* 閲覧専用のため新規予約ボタンは非表示 */}
        <div className="calendar-actions">
          <span style={{ color: '#666', fontSize: '14px' }}>閲覧専用</span>
        </div>
      </div>

      {/* セッション自動復旧中（MSログインへリダイレクト中） */}
      {sessionExpired && (
        <div style={{
          background: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          padding: '16px 24px',
          margin: '20px',
          textAlign: 'center',
          color: '#1e40af',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
        }}>
          <div className="spinner spinner-small spinner-primary"></div>
          <div>Microsoftアカウントに再接続しています...</div>
        </div>
      )}

      {/* エラー表示（セッション切れ以外） */}
      {error && !sessionExpired && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          padding: '16px 24px',
          margin: '20px',
          textAlign: 'center',
          color: '#991b1b'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>アクセスエラー</div>
          <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
        </div>
      )}

      {/* ローディング表示 */}
      {loading && isInitialLoad && (
        <div className="loading-overlay">
          <div className="loading-spinner">読み込み中...</div>
        </div>
      )}

      {loading && !isInitialLoad && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '8px 16px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#666'
        }}>
          <div className="spinner spinner-small spinner-primary"></div>
          更新中...
        </div>
      )}

      {/* 空状態 */}
      {!loading && !sessionExpired && (!reservations || reservations.length === 0) && (
        <EmptyState title="今週の予約はありません" description="Outlookから会議室を予約してください。" />
      )}

      {/* カレンダーグリッド（閲覧専用） */}
      <CalendarGrid
        weekDates={weekDates}
        reservations={reservations}
        onReservationClick={handleReservationClick}
        onEmptySlotClick={() => {}} // 閲覧専用のため無効
        onReservationDrop={() => {}} // 閲覧専用のため無効
        viewMode={viewMode}
        gridRef={gridRef}
        newlyCreatedIds={[]}
        slideDirection={slideDirection}
      />
    </div>
  );
};

export default M365CalendarPage;

